import React, {useEffect, useState} from 'react';
import {
    Accordion,
    AccordionItemProps,
    AccordionProps,
    GridContainer,
    Hyperlink,
    LineTextBlock,
    SearchField,
    Typography,
    VerticalSpacer
} from '../../elements';
import Grid from '@material-ui/core/Grid';
import {AnimateOnViewWrap} from '../../wraps';
import styled from 'styled-components';
import root from 'window-or-global';
import {subNavHeight} from '../../elements/subnav/Subnav.config';
import {useBreakpointDown} from 'epic-ue-theme';
import {useUnrealUIContext} from '../../../shared/context/componentContext';

export interface FAQSectionProps extends AccordionProps {
    titlesSidebar?: boolean;
    title?: string;
    hrefTitle?: string;
    href?: string;
    afterSectionVerticalSpacerProvided?: boolean;
    initialScrollTo?: string;

    //Search Related Props
    resultsTitle?: string;
    noResultsDescription?: string;
    noResultsTitle?: string;
    searchLabelText?: string;
    disableSearchField?: boolean;
}

const Styled = styled.div`
    .search-field .ue-text-field {
        background-color: ${(p) => p.theme.ifLight(p, p.theme.palette.neutral.chalk, 'inherit')};
        color: ${(p) => p.theme.ifLight(p, p.theme.palette.neutral.steel, 'inherit')};
    }
`;

const TitlesSidebar = styled.div`
    ${(p) => p.theme.breakpoints.down('sm')} {
        position: fixed;
        bottom: 0px;
        background-color: ${(p) =>
            p.theme.ifLight(p, p.theme.palette.neutral.pearl, p.theme.palette.neutral.concrete)};
        z-index: 3;
        box-sizing: border-box;
        &.titles-collapsed {
            height: ${(p) => p.theme.spacing(6)}px;
        }
        width: 100%;
        text-align: center;
        padding-top: ${(p) => p.theme.spacing(2)}px;
    }

    a.ue-link-wrap {
        white-space: initial;
        color: ${(p) =>
            p.theme.ifLight(p, p.theme.palette.neutral.steel, p.theme.palette.neutral.silver)};
        &:hover {
            color: ${(p) =>
                p.theme.ifLight(p, p.theme.palette.common.black, p.theme.palette.common.white)};
        }
    }
`;

const checkQuestionMatch = (
    filteredList: AccordionItemProps[],
    filterLower: string,
    item: AccordionItemProps
) => {
    const {title = '', description = ''} = item;
    if (
        title.toLowerCase().indexOf(filterLower) > -1 ||
        description.toLowerCase().indexOf(filterLower) > -1
    ) {
        filteredList.push(item);
    }
};

const getFilteredQuestions = (questionList: AccordionItemProps[], filterStr = '') => {
    if (!filterStr) {
        return questionList;
    }

    const filterLower = filterStr.toLowerCase();
    const filteredList: AccordionItemProps[] = [];

    questionList.forEach((item) => {
        const itemLength = Object.keys(item).length;
        if (item && itemLength) {
            checkQuestionMatch(filteredList, filterLower, item);
        }
    });

    return filteredList;
};
const getTopResultQuestions = (questionList: AccordionItemProps[]) => {
    const topResultsList: AccordionItemProps[] = [];

    questionList.forEach((item) => {
        const itemLength = Object.keys(item).length;
        if (item && itemLength) {
            const {isTopResult} = item;
            isTopResult && topResultsList.push(item);
        }
    });

    return topResultsList;
};

export const FAQSection = ({
    title,
    items = [],
    hrefTitle,
    href,
    defaultExpanded,
    accentColor,
    titlesSidebar,
    closeAllText,
    openAllText,
    afterSectionVerticalSpacerProvided,
    initialScrollTo,
    resultsTitle = 'Search results',
    noResultsDescription,
    noResultsTitle = 'Top Results',
    searchLabelText,
    disableSearchField
}: FAQSectionProps): JSX.Element | null => {
    const isSm = useBreakpointDown('sm');
    const [titlesSidebarCollapsed, setTitlesSidebarCollapsed] = useState(true);
    const initialSection = (items && items[0] && items[0].title) || '';
    const [currentSection, setCurrentSection] = useState(initialSection);
    const {router} = useUnrealUIContext();

    const [searchText, setSearchText] = useState('');
    const filteredView = Boolean(searchText && searchText.length);

    const toggleTitlesSidebarCollapse = (e) => {
        e.preventDefault();
        setTitlesSidebarCollapsed(!titlesSidebarCollapsed);
    };

    const scrollToSection = (e, sectionId) => {
        e && e.preventDefault();
        if (root.__server_side_render) return;

        setSearchText(''); //clear search

        const el = root.document.getElementsByClassName(`sectionId-${sectionId}`);
        if (el) {
            let element = el[0];
            let offsetTop = 0;
            while (element) {
                offsetTop += element.offsetTop;
                element = element.offsetParent;
            }
            root.scrollTopSkip = location.pathname; //prevent ScrollToTop from resetting position for this page
            root.scrollTo({left: 0, top: offsetTop - subNavHeight, behavior: 'smooth'});
        }

        setCurrentSection(items?.[sectionId]?.title || '');
    };

    const getSectionIdByHyphenatedTitle = (initialScrollTo) => {
        return items?.findIndex((item) => {
            return (
                item?.id === initialScrollTo ||
                item?.childPageSlug === initialScrollTo ||
                item?.title?.replace(/\s+/g, '-').toLowerCase() === initialScrollTo
            );
        });
    };

    useEffect(() => {
        if (initialScrollTo) {
            const scrolltoId = getSectionIdByHyphenatedTitle(initialScrollTo.toLowerCase());
            scrolltoId > -1 && scrollToSection('', scrolltoId);
        }
    }, [initialScrollTo, getSectionIdByHyphenatedTitle, scrollToSection]);

    if (!items?.length) {
        return null;
    }

    let noResults = false;
    let masterQuestionList: AccordionItemProps[] = [];
    let filteredQuestionList: AccordionItemProps[] = [];
    let topResultQuestionList: AccordionItemProps[] = [];
    if (filteredView) {
        items?.forEach((item) => {
            const questionList = item?.items || [];
            masterQuestionList = masterQuestionList.concat(questionList);
            if (item.isTopResult) {
                topResultQuestionList = topResultQuestionList.concat(questionList);
            }
        });

        filteredQuestionList = getFilteredQuestions(masterQuestionList, searchText);
        topResultQuestionList = topResultQuestionList.concat(
            getTopResultQuestions(masterQuestionList)
        );
        noResults = !filteredQuestionList || filteredQuestionList.length === 0;
    }

    return (
        <Styled>
            <AnimateOnViewWrap translateY={30}>
                {!disableSearchField && (
                    <GridContainer>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <SearchField
                                    searchLabelText={searchLabelText}
                                    variant="large"
                                    accentColor={accentColor}
                                    onChange={(searchText) => {
                                        setSearchText(searchText);
                                    }}
                                />
                                <VerticalSpacer variant="custom" spacing={7} downSmSpacing={2} />
                            </Grid>
                        </Grid>
                    </GridContainer>
                )}
                {titlesSidebar ? (
                    <>
                        {isSm &&
                            (titlesSidebarCollapsed ? (
                                <TitlesSidebar className="titles-collapsed">
                                    <Typography variant="button" big>
                                        <Hyperlink
                                            accentColor={accentColor}
                                            href={`#`}
                                            onClick={(e) => {
                                                toggleTitlesSidebarCollapse(e);
                                            }}>
                                            {currentSection}
                                        </Hyperlink>
                                    </Typography>
                                </TitlesSidebar>
                            ) : (
                                <TitlesSidebar>
                                    {items?.map((item, index) => (
                                        <React.Fragment key={`title-xs-${item.title}-${index}`}>
                                            <Typography variant="button" big>
                                                <Hyperlink
                                                    accentColor={accentColor}
                                                    key={index}
                                                    href={'#'}
                                                    onClick={(e) => {
                                                        toggleTitlesSidebarCollapse(e);
                                                        scrollToSection(e, `${index}`);
                                                    }}>
                                                    {item.title}
                                                </Hyperlink>
                                            </Typography>
                                            <VerticalSpacer variant="custom" spacing={2} />
                                        </React.Fragment>
                                    ))}
                                </TitlesSidebar>
                            ))}
                        <GridContainer>
                            <Grid container spacing={3}>
                                {!isSm && (
                                    <Grid item sm={12} md={3}>
                                        {
                                            <TitlesSidebar>
                                                <VerticalSpacer variant="custom" spacing={1} />
                                                {items?.map((item, index) => (
                                                    <React.Fragment
                                                        key={`title-${item.title}-${index}`}>
                                                        <Typography variant="button" big>
                                                            <Hyperlink
                                                                accentColor={accentColor}
                                                                key={index}
                                                                href={'#'}
                                                                onClick={(e) => {
                                                                    scrollToSection(e, `${index}`);
                                                                    if (item.id) {
                                                                        router.push({
                                                                            search: `?active=${item.id}`,
                                                                            query: {
                                                                                active: item.id
                                                                            }
                                                                        });
                                                                    }
                                                                }}>
                                                                {item.title}
                                                            </Hyperlink>
                                                        </Typography>
                                                        <VerticalSpacer
                                                            variant="custom"
                                                            spacing={2}
                                                        />
                                                    </React.Fragment>
                                                ))}
                                            </TitlesSidebar>
                                        }
                                    </Grid>
                                )}
                                <Grid item sm={12} md={9}>
                                    {(title || hrefTitle) && (
                                        <LineTextBlock
                                            headline={title}
                                            button={{label: hrefTitle, href}}
                                            accentColor={accentColor}
                                            disableAnim
                                            hideHR
                                        />
                                    )}

                                    {noResults && (
                                        <>
                                            <Typography variant="p">
                                                {noResultsDescription || 'No results'}
                                            </Typography>
                                            <VerticalSpacer variant="custom" spacing={4} />
                                        </>
                                    )}

                                    {!(noResults && !topResultQuestionList) && (
                                        <Accordion
                                            titleAboveItems={true}
                                            closeAllText={closeAllText}
                                            openAllText={openAllText}
                                            accentColor={accentColor}
                                            items={
                                                filteredView
                                                    ? noResults
                                                        ? [
                                                              {
                                                                  title: noResultsTitle,
                                                                  items: topResultQuestionList
                                                              }
                                                          ]
                                                        : [
                                                              {
                                                                  title: resultsTitle,
                                                                  items: filteredQuestionList
                                                              }
                                                          ]
                                                    : items
                                            }
                                            defaultExpanded={defaultExpanded}
                                        />
                                    )}
                                    {!afterSectionVerticalSpacerProvided && (
                                        <VerticalSpacer variant="section" />
                                    )}
                                </Grid>
                            </Grid>
                        </GridContainer>
                    </>
                ) : (
                    <GridContainer>
                        {(title || hrefTitle) && (
                            <LineTextBlock
                                headline={title}
                                button={{label: hrefTitle, href}}
                                accentColor={accentColor}
                                disableAnim
                                hideHR
                            />
                        )}

                        {noResults && (
                            <>
                                <Typography variant="p">
                                    {noResultsDescription || 'No results'}
                                </Typography>
                                <VerticalSpacer variant="custom" spacing={4} />
                            </>
                        )}

                        <Accordion
                            accentColor={accentColor}
                            items={
                                filteredView
                                    ? noResults
                                        ? [
                                              {
                                                  title: noResultsTitle,
                                                  items: topResultQuestionList
                                              }
                                          ]
                                        : [
                                              {
                                                  title: resultsTitle,
                                                  items: filteredQuestionList
                                              }
                                          ]
                                    : items
                            }
                            defaultExpanded={defaultExpanded}
                        />
                        {!afterSectionVerticalSpacerProvided && (
                            <VerticalSpacer variant="section" />
                        )}
                    </GridContainer>
                )}
            </AnimateOnViewWrap>
        </Styled>
    );
};
